/**
 * 同じ線の同じところにいるバスを、縦にずらして積む。
 *
 * ## 線ごとに数える
 *
 * 本線・都市高速・支線・別の道は横位置が違うので、**別の線のバスは重ならない。**
 * 数えるのは同じ線の中だけ。線をまたいで積むと、重なっていないものまで
 * ずらすことになる。
 *
 * 本線だけは向きでも分ける。**上りは帯の左、下りは右**に置いてあるので、
 * 向きが違えば重ならない。ほかの線は向きによらず線の上に乗るので、向きが
 * 違っても矢印のところで重なる — だから一緒に数える。
 *
 * ## 速さが違うものは積まない。**追い抜きは重なってよい**
 *
 * 速さが違う 2 台は放っておけば離れる。無理にずらすと、通り過ぎるだけの
 * 一瞬のために位置を動かすことになる（しかもすれ違うたびに跳ねる）。
 *
 * 直したいのは**ずっと重なったままになるほう** — 同じ停留所に並んで停まって
 * いる 2 台や、同じ区間を同じ所要で走っている 2 台。こちらは離れないので、
 * 積まないと台数が読めない。だから速さ（`advance` の `rate`）ごとに数える。
 *
 * ## 進行方向の後ろへ積む
 *
 * 先頭のバスが本当の位置で、**後続をその後ろへずらす。**前へずらすと、まだ
 * 着いていない停留所にいるように見える。
 *
 * **ずらす量は「先頭からの距離」で決める。**段の高さをそのまま足すと、
 * 元の位置の差が打ち消されて間隔が縮む。下りは後ろ＝上なので、少し先に
 * いる 2 台目を 22px 上げると、**元の差のぶんだけ 1 台目に近づく**
 * （0.05 節＝4px 離れていれば、間隔は 22 ではなく 18px になって重なる）。
 * 先頭の位置に対して段を積み、元の差はそこで引く。
 *
 * ## ずらしたぶんは嘘になる
 *
 * 矢印はその 1 台の居場所を指しているので、ずらした 2 台目以降は本当の位置を
 * 指さない。**台数が読めないほうが困る**ので、そこは譲る。本当の位置は
 * タップして開く詳細のほうに出る。
 */

/** 積むときの 1 段の高さ（px）。**枠の高さと揃える**（.lm-mover は 22px） */
export const STACK_PX = 22;

/** 速さが同じと見なす幅（1 秒あたりの節）。時刻表から出るので差は離れている */
const RATE_EPS = 1e-6;

/**
 * どの線の上の、どの速さか。**重なったままになる組だけを 1 つにする。**
 *
 * 本線は向き（左右）で置き場所が決まり、ほかの線は車線で決まる。
 * 速さを鍵に混ぜるのは、**違う速さの 2 台は放っておけば離れる**ため。
 */
function stackKey(mover) {
  // 時刻表から出る値なので、丸めれば同じ区間どうしはぴたりと揃う
  const rate = Math.round((mover.rate || 0) / RATE_EPS) * RATE_EPS;
  const where = mover.lane === "rail"
    ? `rail/${mover.dir}`
    : `${mover.lane}/${mover.side}/${mover.altLane}/${mover.spurLane}`;

  return `${where}/${rate}`;
}

/**
 * @param {Array<{at: number, dir: string, lane: string, rate?: number}>} movers
 * @param {number} rowPx 1 停留所ぶんの高さ
 * @param {number} stackPx 1 段の高さ
 * @returns {number[]} movers と同じ並びの、縦にずらす量（px）
 */
export function stackOffsets(movers, rowPx, stackPx = STACK_PX) {
  const out = new Array(movers.length).fill(0);

  const groups = new Map();
  movers.forEach((m, i) => {
    const key = stackKey(m);
    if (! groups.has(key)) groups.set(key, []);
    groups.get(key).push(i);
  });

  groups.forEach((indexes) => {
    // 位置の順に見て、1 段ぶんより近いものを 1 つの塊にする
    const sorted = [...indexes].sort((a, b) => movers[a].at - movers[b].at);

    let head = null;
    let step = 0;
    sorted.forEach((i) => {
      const gap = head === null
        ? Infinity
        : Math.abs(movers[i].at - movers[head].at) * rowPx;

      if (gap < stackPx) {
        step += 1;
      } else {
        head = i;
        step = 0;
      }

      if (step === 0) {
        out[i] = 0;   // 先頭は本当の位置。**-0 は作らない**

        return;
      }

      // 進行方向の後ろへ（下りは上、上りは下）。**先頭の位置を基準に積む** —
      // 元の位置の差をここで引かないと、そのぶん間隔が縮んで重なる
      const back = movers[i].dir === "forward" ? -1 : 1;
      const px = back * step * stackPx - (movers[i].at - movers[head].at) * rowPx;
      // 端数を残すと transform に 14 桁並ぶ。1/1000 px は見えない
      out[i] = Math.round(px * 1000) / 1000;
    });
  });

  return out;
}
